// Copyright 2019 Sorama B.V.

import { Disposable } from "aurelia-binding";
import { Quad } from "datatypes/Generic/Quad";
import { Point2D } from "datatypes/SoramaWebApi";

export interface CanvasRegionDrawParent {
    movementMode: MovementMode;
    regionOfInterest: Quad;

    toRegionCoordinate(x: Point2D): Point2D;
    toViewCoordinate(x: Point2D): Point2D;

    postRedraw(): void;
}

export enum MovementMode {
    Horizontal = 0,
    Vertical = 1,
    Both = 2,
}

enum RegionCornerAction {
    Up,
    Down,
    Over,
}

export class CanvasRegion implements Disposable {
    private static readonly cornerRadius = 10; // Pixels

    private regionOfInterest: Quad;
    private alternativeDown = false;
    private cornerActions: [string, RegionCornerAction][] = [
        ["topLeft", RegionCornerAction.Up],
        ["topRight", RegionCornerAction.Up],
        ["bottomRight", RegionCornerAction.Up],
        ["bottomLeft", RegionCornerAction.Up],
    ];

    constructor(
        private readonly touchElement: HTMLElement,
        private readonly parent: CanvasRegionDrawParent
    ) {
        this.regionOfInterest = Quad.clone(this.parent.regionOfInterest);
        this.touchElement.addEventListener("mousemove", this.onMouseMove);
        this.touchElement.addEventListener("mousedown", this.onMouseDown);
        this.touchElement.addEventListener("mouseup", this.onMouseUp);
    }

    public dispose() {
        this.touchElement.removeEventListener("mousemove", this.onMouseMove);
        this.touchElement.removeEventListener("mousedown", this.onMouseDown);
        this.touchElement.removeEventListener("mouseup", this.onMouseUp);
    }

    public draw(context: CanvasRenderingContext2D): void {
        const pts = this.cornerActions.map((x) => {
            return this.parent.toViewCoordinate(this.regionOfInterest[x[0]]);
        });

        // Draw lines
        context.strokeStyle = "white";
        context.shadowBlur = 3;
        context.shadowColor = "rgba(0,0,0,.6)";
        context.beginPath();
        context.moveTo(pts[0].x, pts[0].y);
        context.lineTo(pts[1].x, pts[1].y);
        context.lineTo(pts[2].x, pts[2].y);
        context.lineTo(pts[3].x, pts[3].y);
        context.closePath();
        context.stroke();

        // Draw circles
        context.strokeStyle = "black";
        context.lineWidth = 3;

        context.fillStyle = this.cornerActionToColor(this.cornerActions[0][1]);
        this.drawCircle(context, pts[0].x, pts[0].y, CanvasRegion.cornerRadius);
        context.fillStyle = this.cornerActionToColor(this.cornerActions[1][1]);
        this.drawCircle(context, pts[1].x, pts[1].y, CanvasRegion.cornerRadius);
        context.fillStyle = this.cornerActionToColor(this.cornerActions[2][1]);
        this.drawCircle(context, pts[2].x, pts[2].y, CanvasRegion.cornerRadius);
        context.fillStyle = this.cornerActionToColor(this.cornerActions[3][1]);
        this.drawCircle(context, pts[3].x, pts[3].y, CanvasRegion.cornerRadius);
    }

    private drawCircle(context: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
        context.beginPath();
        context.arc(x, y, radius, 0, 2 * Math.PI);
        context.stroke();
        context.fill();
    }

    private onMouseMove = (mouseEvent: MouseEvent) => {
        if (this.alternativeDown) {
            const p0 = this.parent.toRegionCoordinate({
                x: mouseEvent.offsetX - mouseEvent.movementX,
                y: mouseEvent.offsetY - mouseEvent.movementY,
            });
            const p1 = this.parent.toRegionCoordinate({ x: mouseEvent.offsetX, y: mouseEvent.offsetY });
            const dx = p1.x - p0.x;
            const dy = p1.y - p0.y;

            this.cornerActions.forEach((x) => {
                const p = this.regionOfInterest[x[0]];
                if (
                    this.parent.movementMode == MovementMode.Horizontal ||
                    this.parent.movementMode == MovementMode.Both
                ) {
                    p.x += dx;
                }

                if (
                    this.parent.movementMode == MovementMode.Vertical ||
                    this.parent.movementMode == MovementMode.Both
                ) {
                    p.y += dy;
                }
            });
        } else {
            const downCorner = this.cornerActions.find((x) => x[1] === RegionCornerAction.Down);
            if (downCorner) {
                const p: Point2D = this.parent.toRegionCoordinate({ x: mouseEvent.offsetX, y: mouseEvent.offsetY });
                if (this.parent.movementMode === MovementMode.Horizontal) {
                    p.y = this.regionOfInterest[downCorner[0]].y;
                }
                if (this.parent.movementMode === MovementMode.Vertical) {
                    p.x = this.regionOfInterest[downCorner[0]].x;
                }

                // Update region
                this.regionOfInterest[downCorner[0]] = p;
            } else {
                this.onMouseAction(mouseEvent, RegionCornerAction.Over);
            }
        }

        this.parent.postRedraw();
    };

    private onMouseDown = (mouseEvent: MouseEvent): void => {
        if (mouseEvent.button != 0x0) return;

        if (mouseEvent.altKey) {
            // Set all to down
            this.cornerActions.forEach((x) => (x[1] = RegionCornerAction.Down));
            this.alternativeDown = true;
        } else {
            this.onMouseAction(mouseEvent, RegionCornerAction.Down);
        }

        this.parent.postRedraw();
    };

    private onMouseUp = (mouseEvent: MouseEvent): void => {
        if (mouseEvent.button != 0x0) return;

        if (this.cornerActions.some((x) => x[1] == RegionCornerAction.Down)) {
            // Set region of interest on parent
            this.parent.regionOfInterest = this.regionOfInterest;
        }

        this.alternativeDown = false;
        this.onMouseAction(mouseEvent, RegionCornerAction.Over);
        this.parent.postRedraw();
    };

    private onMouseAction(mouseEvent: MouseEvent, action: RegionCornerAction): void {
        const pts = this.cornerActions.map((x) => {
            return this.parent.toViewCoordinate(this.regionOfInterest[x[0]]);
        });

        for (let i = 0; i < 4; i++) {
            const p = pts[i];

            const delta = Math.sqrt(Math.pow(p.x - mouseEvent.offsetX, 2) + Math.pow(p.y - mouseEvent.offsetY, 2));
            if (delta < CanvasRegion.cornerRadius) {
                // Hit!
                this.cornerActions[i][1] = action;
            } else {
                this.cornerActions[i][1] = RegionCornerAction.Up;
            }
        }
    }

    private cornerActionToColor(cornerAction: RegionCornerAction): string {
        switch (cornerAction) {
            case RegionCornerAction.Down:
                return "darkorange";
            case RegionCornerAction.Over:
                return "carrot";
            default:
                return "white";
        }
    }
}
